<template>
  <div v-if="book && genres.length > 0">
    <div
      class="toolbar hide-during-transition show-on-load"
      :class="{ 'show-toolbar': showToolbar }"
    >
      <div class="toolbar-push">
        <v-container pa-0 class="toolbar-container">
          <v-layout row wrap>
            <v-flex xs12 sm12 md4>
              <div class="toolbar-title">New Book</div>
            </v-flex>
            <v-flex xs12 sm12 md4>
              <div class="toolbar-progress">
                <div
                  class="toolbar-progress-percentage"
                  v-if="progressPercentage < 99"
                >
                  {{ progressPercentage }}% complete
                </div>
                <div v-else class="submit-container hidden-sm-and-down">
                  <div class="submit-button" @click="submitForApproval">
                    Submit For Approval
                  </div>
                </div>
              </div>
            </v-flex>
            <v-flex xs12 sm12 md4 class="hidden-sm-and-down">
              <div class="toolbar-status" v-if="!saveInProgress">Saved</div>
              <div class="toolbar-status" v-else>Saving</div>
            </v-flex>
          </v-layout>
        </v-container>
      </div>
    </div>

    <div class="toolbar-space"></div>

    <div class="hidden-md-and-up save-message">
      This page auto-saves when making changes.
    </div>

    <div
      class="upgrade-required"
      v-if="requiresPlanUpgrade"
    >
      <div class="upgrade-message">
        You'll need to upgrade your plan before you can submit this book.
      </div>
      <br class="hidden-md-and-up" />
      <div class="upgrade-button" @click="clickUpgrade">Upgrade</div>
    </div>

    <v-container class="form-container content-last">
      <form-section name="basics" :status="sections.basics.status">
        <greeting
          v-if="sections.basics.status === 'active'"
          wave="true"
          boldMessage="Hey there! Got a new book?"
          message="Awesome! First, let's start with the basics."
        ></greeting>
        <field
          title="Title"
          instructions="Provide the full title of your book."
          :status="fieldStatuses.title"
          :error="fieldErrors.title"
        >
          <v-text-field
            v-model.trim="$v.title.$model"
            label
            required
            class="title-field"
            :disabled="editLocked"
            @keyup="changeField('title')"
            @blur="blurField('title')"
          ></v-text-field>
        </field>

        <field
          title="Author"
          instructions="Provide the author of your book."
          :status="fieldStatuses.author"
          :error="fieldErrors.author"
        >
          <v-text-field
            v-model.trim="$v.author.$model"
            label
            required
            class="author-field"
            :disabled="editLocked"
            @keyup="changeField('author')"
            @blur="blurField('author')"
          ></v-text-field>
        </field>

        <div class="buttons" v-if="sections.basics.status === 'active'">
          <div
            class="continue-button action"
            @click="
              blurField('title');
              blurField('author');
            "
          >
            Continue
          </div>
        </div>
      </form-section>

      <reader-upgrades
        v-if="
          sections.readers.status === 'active' ||
          sections.readers.status === 'done'
        "
        :book="book"
        :onComplete="onChooseReadersCompleted"
        :onError="onChooseReadersError"
        :onManuscriptComplete="onManuscriptComplete"
        :onManuscriptRemove="onManuscriptRemove"
        :savedManuscriptUrl="book.manuscript_file_url"
      ></reader-upgrades>

      <form-section name="files" :status="sections.files.status">
        <greeting
          v-if="sections.files.status === 'active'"
          boldMessage="Sounds interesting!"
          message="We just need one file: your book cover. Find that sucker and drag it in below."
        ></greeting>
        <v-container>
          <v-layout row wrap>
            <v-flex md3></v-flex>
            <v-flex xs12 sm12 md6>
              <transition name="slide-fade">
                <div>
                  <field
                    title="Front Cover"
                    isFile="true"
                    instructions
                    :disabled="editLocked"
                    :status="fieldStatuses.cover"
                    :error="fieldErrors.cover"
                  >
                    <div v-if="book.cover_file_url">
                      <div class="cover-display">
                        <img
                          :src="book.cover_file_url | s3Path"
                          alt
                          class="cover-display-img"
                        />
                      </div>
                      <div v-if="!editLocked" class="remove-file" @click="removeCover">
                        Remove File
                      </div>
                    </div>
                    <div v-else>
                      <vue-dropzone
                        ref="coverDropzone"
                        id="dropzone"
                        :options="dropzoneCoverOptions"
                        v-on:vdropzone-complete="coverComplete"
                      ></vue-dropzone>
                      <div class="upload-instructions">
                        <b>Accepted file types: .jpg, .png, .gif</b>
                        <br />
                        <div @click="toggleModal" class="blue-text"><i class="inline-icon material-icons">visibility</i> Cover File Examples</div>
                        <transition name="slide-fade" mode="in-out">
                        <InfoModal v-if="this.modalShow" title="Cover File Example" @close="toggleModal">
                          <v-container grid-list-lg text-xs-center>
                              <v-layout row wrap justify-center>
                                <v-flex xs1 sm1 md3>
                                  <v-card>
                                    <img class="example-img" src="/images/books/cover-eg-3d.jpg" />
                                    <v-card-text>3D Covers are cool, but not accepted</v-card-text>
                                  </v-card>
                                </v-flex>
                                <v-flex xs1 sm1 md3>
                                  <v-card ma3 pa1 mx-lg-auto >
                                    <img class="example-img" src="/images/books/cover-eg-net.jpg" />
                                    <v-card-text>Full Jacket Layouts are not accepted</v-card-text>
                                  </v-card>
                                </v-flex>
                                <v-flex xs1 sm1 md3>
                                  <v-card md3 mx-lg-auto justify-center>
                                    <img class="example-img" src="/images/books/cover-eg-ok.jpg" />
                                    <v-card-text>You need a flat, front cover image</v-card-text>
                                  </v-card>
                                </v-flex>
                              </v-layout>
                          </v-container>
                        </InfoModal>
                      </transition>
                        Please provide the <span class="emphasis">2D image of your front cover only</span>. We do not accept 3D models or front-spine-back covers. 
                        To grab your exact Amazon cover, you can go to your book's Amazon page, then right-click on the cover image, and click 'Save image as.'
                      </div>
                    </div>
                  </field>
                </div>
              </transition>
            </v-flex>
            <v-flex xs12 sm12 md6 style="display: none">
              <field
                title="Manuscript"
                isFile="true"
                instructions
                :status="fieldStatuses.manuscript"
                :error="fieldErrors.manuscript"
              >
                <div v-if="book.manuscript_file_url">
                  <div class="manuscript-display">
                    <div class="manuscript-done">
                      <i class="material-icons manuscript-done-icon"
                        >cloud_done</i
                      >
                    </div>
                    <div class="manuscript-done-filetype">
                      {{ book.manuscript_file_url.split(".").pop() }}
                    </div>
                  </div>
                  <div class="remove-file" @click="removeManuscript">
                    Remove File
                  </div>
                </div>
                <div v-else>
                  <vue-dropzone
                    ref="manuscriptDropzone"
                    id="dropzone"
                    :options="dropzoneManuscriptOptions"
                    v-on:vdropzone-complete="manuscriptComplete"
                  ></vue-dropzone>
                  <div class="upload-instructions">
                    Accepted file types: .epub, .pdf
                    <br />Your manuscript will be securely encrypted and only
                    made accessible to readers who have already agreed to review
                    your book.
                  </div>
                </div>
              </field>
            </v-flex>
          </v-layout>
        </v-container>
      </form-section>

      <form-section name="more" :status="sections.more.status">
        <greeting
          v-if="sections.more.status === 'active'"
          boldMessage="Now for some easy things!"
          message="Point us to your book on Amazon, and tell us the genre you're in."
        ></greeting>

        <field
          title="Amazon URL"
          instructions="Provide the full URL to your book on Amazon."
          :status="fieldStatuses.amazonUrl"
          :error="fieldErrors.amazonUrl"
        >
          <v-text-field
            v-model.trim.lazy="$v.amazonUrl.$model"
            label
            required
            :disabled="editLocked"
            class="url-field"
            @keyup="changeField('amazonUrl')"
            @blur="blurField('amazonUrl')"
          ></v-text-field>
        </field>

        <field
          title="Genre"
          :instructions="genreFieldInstructions"
          :status="fieldStatuses.genre"
          :error="fieldErrors.genre"
        >
          <v-select
            v-model="$v.genre.$model"
            :items="genres"
            @change="changeField('genre')"
            @blur="blurField('genre')"
          >
            <template slot="selection" slot-scope="data">{{
              data.item.label
            }}</template>
            <template slot="item" slot-scope="data">{{
              data.item.label
            }}</template>
          </v-select>
          <v-select
            v-if="genre && genre.subgenres.length > 0"
            v-model="$v.subgenre.$model"
            :items="genre.subgenres"
            @change="changeField('genre')"
            @blur="blurField('genre')"
          >
            <template slot="selection" slot-scope="data">{{
              data.item.label
            }}</template>
            <template slot="item" slot-scope="data">{{
              data.item.label
            }}</template>
          </v-select>
        </field>

        <field
          title="Language"
          instructions="Please Select the language your book is written in"
          :status="fieldStatuses.language"
          :error="fieldErrors.language"
        >
          <v-select
            v-model="$v.language.$model"
            :items="languages"
            @change="changeField('language')"
            @blur="blurField('language')"
          >
            <template slot="selection" slot-scope="data">{{
              data.item.label
            }}</template>
            <template slot="item" slot-scope="data">{{
              data.item.label
            }}</template>
          </v-select>
        </field>

        <div class="explicit-content">
          <summary-option
            id="explicitContent"
            :checked="book.explicit_content"
            label="Explicit content?"
            listName="explicitContent"
            v-on:toggle-option="toggleExplicitContent($event)"
          ></summary-option>
          <div class="clearfix"></div>
        </div>
      </form-section>

      <form-section name="summaries" :status="sections.summaries.status">
        <greeting
          v-if="sections.summaries.status === 'active'"
          boldMessage="Last part!"
          message="Readers want to know about your book. Answer the prompts below for them."
        ></greeting>
        <v-container>
          <v-layout row wrap class="section">
            <!-- <v-flex xs12 md4>
              <div class="section-status">
                <div class="section-status-circle"></div>
              </div>
              <div class="section-heading">Summary</div>
              <div class="section-instructions">Provide the summary of your book.</div>
            </v-flex>-->
            <v-flex xs12>
              <template v-if="currentGenre">
                <template v-for="(prompt, i) in currentGenre.summary_prompts">
                  <div class="prompt-field">
                    <div class="prompt-sentence">{{ prompt.question }}</div>
                    <div class="prompt-examples" v-if="prompt.examples">
                      Examples: {{ prompt.examples }}
                    </div>
                    <div class="value">
                      <textarea
                        class="prompt-answer"
                        v-bind:style="{ height: prompt.height + 'px' }"
                        v-if="prompt.type === 'open'"
                        v-model="
                          summaryAnswers[currentGenre.id.toString()][
                            prompt.name
                          ]
                        "
                        :key="currentGenre.id.toString() + '-' + prompt.name"
                        @blur="save"
                        @keyup="updateSummaryStatuses"
                      ></textarea>
                      <div v-if="prompt.type === 'list'">
                        <summary-option
                          v-for="option in prompt.values"
                          :key="
                            currentGenre.id.toString() +
                            '-' +
                            prompt.name +
                            '-' +
                            option
                          "
                          :id="option"
                          :checked="
                            summaryAnswers[currentGenre.id.toString()][
                              prompt.name
                            ] &&
                            summaryAnswers[currentGenre.id.toString()][
                              prompt.name
                            ][option]
                          "
                          :label="option"
                          :listName="prompt.name"
                          v-on:toggle-option="toggleSummaryListOption($event)"
                        ></summary-option>
                      </div>
                      <div v-if="prompt.type === 'custom_list'">
                        <div
                          v-for="i in 8"
                          :key="i"
                          class="summary-custom-list-item-container"
                        >
                          <div class="summary-custom-list-item-number">
                            &bull;
                          </div>
                          <input
                            v-model="
                              summaryAnswers[currentGenre.id.toString()][
                                prompt.name
                              ][i]
                            "
                            type="text"
                            class="summary-custom-list-item"
                            @blur="save"
                          />
                        </div>
                      </div>
                      <div class="clearfix"></div>
                      <div
                        class="summary-error"
                        v-if="summaryErrors[prompt.name]"
                      >
                        {{ summaryErrors[prompt.name] }}
                      </div>
                    </div>
                  </div>
                </template>
                <div class="submit-bottom-container">
                  <div class="submit-button" @click="submitForApproval">
                    Submit For Approval
                  </div>
                </div>
              </template>
            </v-flex>
          </v-layout>
        </v-container>
      </form-section>
    </v-container>
  </div>
</template>

<script>
import Head from "@/components/Page/Head";
import Heading from "@/components/Page/Heading";
import BookStatus from "../values/BookStatus";
import Section from "../components/Form/Section";
import Field from "../components/Form/Field";
import Option from "../components/Form/Option";
import Greeting from "../components/Form/Greeting";
import ReaderUpgrades from "@/components/ReaderUpgrades/ReaderUpgrades";
import Back from "../modules/Back";
import Bus from "../modules/Bus";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import InfoModal from "../components/Form/InfoModal";
//import UploadButton from "vuetify-upload-button";
import { mapState } from "vuex";
import { required, minLength, between } from "vuelidate/lib/validators";
import { min } from "moment";

export default {
  components: {
    vueDropzone: vue2Dropzone,
    "form-section": Section,
    ReaderUpgrades,
    Head,
    Heading,
    Field,
    "summary-option": Option,
    Greeting,
    InfoModal,
    //"upload-btn": UploadButton
  },
  data: function () {
    return {
      bookId: this.$route.params.id || null,
      sections: {
        basics: {
          status: "active",
        },
        readers: {
          status: "waiting",
        },
        files: {
          status: "waiting",
        },
        more: {
          status: "waiting",
        },
        summaries: {
          status: "waiting",
        },
      },
      fieldStatuses: {
        title: "Pending",
        author: "Pending",
        cover: "Pending",
        manuscript: "Pending",
        amazonUrl: "Pending",
        genre: "Pending",
        language: "Pending",
      },
      fieldLocks: {
        title: true,
        author: true,
        cover: true,
        manuscript: true,
        amazonUrl: true,
        genre: true,
        language: true,
      },
      fieldErrors: {
        title: null,
        author: null,
        cover: null,
        manuscript: null,
        amazonUrl: null,
        genre: null,
        language: null,
      },
      fieldLabels: {
        title: "title",
        author: "author",
        cover: "cover",
        manuscript: "manuscript",
        amazonUrl: "Amazon URL",
        genre: "genre",
        language: "language"
      },
      progressPercentage: 0,
      saveLabel: "Saved",
      saveInProgress: false,
      selectedGenre: null,
      book: null,
      title: null,
      author: null,
      genre: null,
      subgenre: null,
      language: null,
      explicitContent: null,
      amazonUrl: null,
      cover: null,
      manuscript: null,
      coverFileUrl: null,
      manuscriptFileUrl: null,
      summaryAnswers: {},
      summaryStatuses: {},
      summaryErrors: {},
      dropzoneCoverOptions: null,
      dropzoneManuscriptOptions: null,
      promptSentences: {
        big_question: "What is the main topic of your book?",
        big_answer: "What is the main plot resolution of your book?",
      },
      fieldErrorTemplates: {
        title: "The title is a little too short.",
        author: "The author is a little too short.",
        cover: null,
        manuscript: null,
        amazonUrl: null,
        genre: "",
        language: "",
      },
      submitError: null,
      submitTouched: false,
      isNew: false,
      canSave: false,
      showToolbar: true,
      modalShow:false,
      editLocked:false,
    };
  },
  validations: {
    title: {
      required,
      minLength: minLength(3),
    },
    author: {
      required,
      minLength: minLength(3),
    },
    genre: {
      required,
    },
    subgenre: {},
    language: {
      required,
    },
    amazonUrl: {
      required,
      minLength: minLength(12),
    },
    cover: {},
    manuscript: {},
  },
  watch: {
    genre: function () {
      this.updateStatus("genre");
      this.save();
    },
    genres: function() {
      if (this.genres.length > 0 && this.book.genre_id && !this.genre) {
        this.genre = findGenre(this.genres, this.book.genre_id)
        if (this.book.subgenre_id) {
          this.subgenre = findGenre(this.genre.subgenres, this.book.subgenre_id);
        }
      }
    },
    languages: function() {
      if (this.languages.length > 0 && this.book.language_id && !this.language) {
        this.language = findGenre(this.languages, this.book.language_id)
        this.updateStatus("language");
      }
    },
  },
  computed: {
    ...mapState({
      genres: "genres",
      languages: "languages"
    }),
    requiresPlanUpgrade: function() {
      if (!this.$store.state.user) {
        return true;
      }
      if (this.$store.state.user.isFree) {
        return true;
      }
      if (this.$store.state.user.isAllottedBooksFull && this.book) {
        return (this.book.status !== 'Available' &&
        this.book.status !== 'PendingApproval' &&
        this.book.status !== 'ChangesRequested')
      }
      return false
    },
    currentGenre: function () {
      if (!this.book || !this.genre || this.genres.length <= 0) {
        return 0;
      }
      return findGenre(this.genres, this.genre.id);
    },
    genreFieldInstructions: function() {
      if (this.genre && this.genre.subgenres.length > 0) {
        return "Select the genre and optional subgenre of your book."
      }
      return "Select the genre of your book."
    },
    requiresApproval: function () {
      if (!this.book) {
        return false;
      }
      return BookStatus.requiresApprovalToChange[this.book.status];
    },
  },
  beforeDestroy() {
    this.showToolbar = false;
  },
  async mounted() {
    let book = null;

    if (this.bookId) {
      book = await Back.book(this.bookId);
    } else {
      book = newBook();
      this.isNew = true;
    }

    this.book = book;
    this.bookId = book.id;

    if (this.book.available_at) {
      this.editLocked = true;
    }

    if (this.book.genre_id && this.genres.length > 0) {
      this.genre = findGenre(this.genres, this.book.genre_id);
    }

    if (this.genre && this.book.subgenre_id) {
      this.subgenre = findGenre(this.genre.subgenres, this.book.subgenre_id);
    }

    this.summaryAnswers = book.summary;
    this.title = book.title;
    this.author = book.author;
    this.coverFileUrl = book.cover_file_url;
    this.manuscriptFileUrl = book.manuscript_file_url;
    this.amazonUrl = book.web_url;

    if (this.languages.length > 0 && this.book.language_id) {
      this.language = findGenre(this.languages, this.book.language_id)
    } else if (this.languages.length > 0) {
      this.language = findGenre(this.languages, 2)
    }

    // this.summaryAnswers = this.book.summary;

    this.updateStatus("title");
    this.updateStatus("author");
    this.updateStatus("amazonUrl");
    this.updateStatus("genre");
    this.updateStatus("language");
    // this.updateStatus("cover");

    if (this.book.cover_file_url) {
      this.fieldStatuses.cover = "Completed";
      this.sections.files.status = "done";
    }
    if (this.manuscriptFileUrl) {
      this.fieldStatuses.manuscript = "Completed";
    }

    if (
      this.book.is_standard_readers ||
      this.book.is_kindle_readers ||
      this.book.is_verified_readers
    ) {
      this.chooseReadersStatus = "Completed";
      this.sections.readers.status = "done";
    }

    this.decideSectionStatus(true);

    if (this.genres.length > 0) {
      for (const g in this.genres) {
        const genre = this.genres[g];
        const genreKey = genre.id.toString();
        const genrePrompts = genre.summary_prompts;

        if (
          !this.summaryAnswers[genreKey] ||
          this.summaryAnswers[genreKey] instanceof Array
        ) {
          this.summaryAnswers[genreKey] = {};
        }

        for (const p in genrePrompts) {
          const prompt = genrePrompts[p];
          if (!this.summaryAnswers[genreKey][prompt.name]) {
            if (prompt.type === "custom_list") {
              this.summaryAnswers[genreKey][prompt.name] = {};
            } else {
              this.summaryAnswers[genreKey][prompt.name] = "";
            }
          }
        }
      }
    }

    this.dropzoneCoverOptions = {
      url: Back.coverUploadUrl(this.book),
      acceptedFiles: "image/*",
      thumbnailWidth: 150,
      resizeWidth: 308,
      maxFilesize: 24,
      timeout: 90000,
      headers: {},
    };
    this.dropzoneManuscriptOptions = {
      url: Back.manuscriptUploadUrl(this.book),
      acceptedFiles: ".pdf,.epub",
      thumbnailWidth: 150,
      maxFilesize: 200,
      timeout: 90000,
      headers: {},
    };

    this.updateProgress();
    this.canSave = true;
  },
  methods: {
    coverComplete(response) {
      const responseJson = response.xhr.responseText;
      const responseBody = JSON.parse(responseJson);

      if (!responseBody || !responseBody.success) {
        this.fieldStatuses.cover = "Error";
        return;
      }

      this.fieldStatuses.cover = "Completed";
      this.coverFileUrl = responseBody.data.file_url;

      this.decideSectionStatus();
      this.save();
    },
    manuscriptComplete(response) {
      const responseJson = response.xhr.responseText;
      const responseBody = JSON.parse(responseJson);

      if (!responseBody || !responseBody.success) {
        this.fieldStatuses.manuscript = "Error";
        return;
      }

      this.fieldStatuses.manuscript = "Completed";
      this.manuscriptFileUrl = responseBody.data.file_url;
      this.decideSectionStatus();
      this.save();
    },

    async onChooseReadersCompleted(isStandard, isKindle, isVerified) {
      this.book.is_standard_readers = isStandard;
      this.book.is_kindle_readers = isKindle;
      this.book.is_verified_readers = isVerified;

      // console.log("onChooseReadersCompleted", this.book);

      this.chooseReadersStatus = "Completed";
      this.decideSectionStatus();

      await this.save();
    },
    onChooseReadersError() {
      this.chooseReadersStatus = "Error";
      this.decideSectionStatus();
    },

    onManuscriptComplete(url) {
      // console.log("onManuscriptComplete", url);
      this.manuscriptFileUrl = url;
      this.save();
    },
    onManuscriptRemove() {
      this.manuscriptFileUrl = null;
      this.save();
    },
    updateStatus(field, ignoreErrors) {
      const status = this.decideStatus(field);
      this.fieldStatuses[field] = status;

      if (status === "Error" && !ignoreErrors) {
        this.fieldErrors[field] = this.decideError(field);
      } else {
        this.fieldErrors[field] = null;
      }

      this.save();
    },
    decideStatus(field) {
      const fieldState = this.$v[field];

      this.decideSectionStatus();

      if (fieldState.$error) {
        return "Error";
      }

      if (fieldState.$invalid) {
        return "Pending";
      }

      if (fieldState.$invalid === false) {
        return "Completed";
      }
    },
    decideError(field) {
      const fieldState = this.$v[field];

      if (!fieldState.required) {
        return "The " + this.fieldLabels[field] + " of your book is required.";
      }
      if (!fieldState.minLength) {
        return "The " + this.fieldLabels[field] + " is too short.";
      }
    },
    decideSectionStatus(refresh) {
      if (refresh || this.sections.basics.status === "active") {
        if (
          this.$v.title.$invalid === false &&
          this.$v.author.$invalid === false
        ) {
          this.sections.basics.status = "done";
          setTimeout(this.enterNextSection, 300);
        } else {
          //this.sections.basics.status = "active";
        }
      }

      if (refresh || this.sections.readers.status === "active") {
        if (this.chooseReadersStatus === "Completed") {
          this.sections.readers.status = "done";
          setTimeout(this.enterNextSection, 300);
        } else {
          //this.sections.files.status = "active";
        }
      }

      if (refresh || this.sections.files.status === "active") {
        if (this.fieldStatuses.cover === "Completed") {
          this.sections.files.status = "done";
          setTimeout(this.enterNextSection, 300);
        } else {
          //this.sections.files.status = "active";
        }
      }

      if (refresh || this.sections.more.status === "active") {
        if (
          this.$v.amazonUrl.$invalid === false &&
          this.$v.genre.$invalid === false
        ) {
          this.sections.more.status = "done";
          this.sections.summaries.status = "active";
          setTimeout(this.enterNextSection, 300);
        } else {
          //this.sections.more.status = "active";
        }
      }
    },
    updateSummaryStatuses() {
      // console.log("updateBug start");
      if (!this.currentGenre) {
        // console.log("updateBug currentGenre");
        return;
      }

      for (const p in this.currentGenre.summary_prompts) {
        const prompt = this.currentGenre.summary_prompts[p];

        const answer =
          this.summaryAnswers[this.currentGenre.id.toString()][prompt.name];
        // console.log("updateBug prompt", prompt.name, answer);

        let status = this.decideSummaryStatus(prompt, answer);
        // console.log("updateBug decideSummaryStatus", status);
        let error = null;
        if (status !== "Pending" && status !== "Completed") {
          error = status;
          status = "Error";
        }

        this.summaryStatuses[prompt.name] = status;
        this.summaryErrors[prompt.name] = error;
      }

      // console.log("updateBug done", this.summaryErrors, this.summaryStatuses);
      this.$forceUpdate();
    },
    decideSummaryStatus(prompt, answer) {
      if (prompt.minimum === -1) {
        return "Completed";
      }

      if (!this.submitTouched) {
        if (!answer) {
          return "Pending";
        }

        if (typeof answer === "object" && countObject(answer) === 0) {
          return "Pending";
        }
      }

      let minimum = null;
      let length = null;

      switch (prompt.type) {
        case "open":
          minimum = prompt.minimum || 1;
          length = countWords(answer);

          if (length < minimum) {
            return (
              "" +
              length +
              " words written. " +
              "Please write at least " +
              minimum +
              " words."
            );
          }
          return "Completed";
          break;
        case "list":
          minimum = Number.isInteger(prompt.minimum) ? prompt.minimum : 1;
          length = countObject(answer);

          if (length < minimum) {
            return (
              length +
              " items chosen. " +
              "Please choose at least " +
              minimum +
              " items."
            );
          }
          return "Completed";
          break;
        case "custom_list":
          minimum = prompt.minimum || 1;
          length = countObject(answer);

          if (length < minimum) {
            return (
              length +
              " items filled. " +
              "Please fill out at least " +
              minimum +
              " items."
            );
          }
          return "Completed";
          break;
      }

      return "Pending";
    },
    enterNextSection() {
      for (const s in this.sections) {
        const section = this.sections[s];
        // console.log("next section?", section);
        if (section.status !== "done") {
          section.status = "active";
          // console.log("enter section", section);
          return;
        }
      }
    },
    changeField(field) {
      if (this.fieldLocks[field]) {
        this.updateStatus(field, true);
        return;
      }
      this.$v[field].$touch();
      this.updateStatus(field);
    },
    blurField(field) {
      this.fieldLocks[field] = false;
      this.$v[field].$touch();
      this.updateStatus(field);
    },
    save() {
      if (!this.canSave) {
        console.log("cannot save yet");
        return;
      }

      if (this.genres.length <= 0 || this.languages.length <= 0) {
        console.log("cannot save before loading");
        return;
      }

      this.book.title = this.title;
      this.book.author = this.author;
      this.book.cover_file_url = this.coverFileUrl;
      this.book.manuscript_file_url = this.manuscriptFileUrl;
      this.book.summary = this.summaryAnswers;
      this.book.language_id = this.language ? this.language.id : null;
      this.book.genre_id = this.genre ? this.genre.id : null;
      this.book.subgenre_id = this.subgenre ? this.subgenre.id : null;
      this.book.web_url = this.amazonUrl;
      this.book.explicit_content = this.explicitContent;

      console.log("saving");

      this.saveInProgress = true;
      this.canSave = false;
      Back.saveBook(this.book)
        .then((response) => {
          this.saveInProgress = false;
          this.canSave = true;
          if (!this.book.id && response.id) {
            this.book.id = response.id;
            this.bookId = this.book.id;
          }

          if (this.isNew) {
            this.New = false;
            //this.$router.replace("/books/edit/" + this.book.id);
            history.replaceState({}, "Title", "/#/books/edit/" + this.book.id);
          }
        })
        .catch((err) => {
          this.saveInProgress = false;
          this.canSave = true;
        });

      this.updateProgress();
      //this.$forceUpdate();
    },
    updateProgress() {
      this.updateSummaryStatuses();

      let progressPercentage = 0;
      const progressAllotments = {
        title: 10,
        author: 10,
        cover: 30,
        manuscript: 0,
        amazonUrl: 10,
        genre: 10,
        //summaries: 30
      };

      for (const progressField in progressAllotments) {
        if (this.fieldStatuses[progressField] === "Completed") {
          progressPercentage += progressAllotments[progressField];
        }
      }

      for (const s in this.summaryStatuses) {
        if (this.summaryStatuses[s] === "Completed") {
          progressPercentage += 30 / countObject(this.summaryStatuses);
        }
      }

      if (progressPercentage > 100) {
        progressPercentage = 100;
      }

      this.progressPercentage = Math.ceil(progressPercentage);
    },
    clickUpgrade() {
      window.location.href =
        process.env.VUE_APP_ACCOUNT_URL + "/settings/#/subscription";
    },
    submitForApproval() {
      this.submitTouched = true;
      const notCompleted = [];
      const summaryErrorMessages = [];

      this.blurField("title");
      this.blurField("author");
      this.blurField("genre");
      this.blurField("amazonUrl");

      this.updateSummaryStatuses();

      for (const field in this.fieldStatuses) {
        if (
          this.fieldStatuses[field] !== "Completed" &&
          this.fieldLabels[field] !== "manuscript"
        ) {
          notCompleted.push(this.fieldLabels[field]);
        }
      }

      let isSummaryCompleted = true;
      for (const prompt in this.summaryStatuses) {
        if (this.summaryStatuses[prompt] !== "Completed") {
          isSummaryCompleted = false;

          var promptMessage =
            "Oops! Please review your Summary answers. Some of your answers need more information.";
          for (const i in this.currentGenre.summary_prompts) {
            const promptLookup = this.currentGenre.summary_prompts[i];
            // console.log("lookup", promptLookup, prompt);
            if (promptLookup.name === prompt) {
              switch (promptLookup.type) {
                case "open":
                  promptMessage =
                    "Oops! Please write a little more for your '" +
                    promptLookup.question.replace(":", "") +
                    "' answer.";
                  break;
                case "list":
                  promptMessage =
                    "Oops! Please add more items to your '" +
                    promptLookup.question.replace(":", "") +
                    "' answer.";
                  break;
                case "custom_list":
                  promptMessage =
                    "Oops! Please add more items to your '" +
                    promptLookup.question.replace(":", "") +
                    "' answer.";
                  break;
              }
            }
          }

          this.$toasted.show(promptMessage, {
            icon: "error",
            position: "bottom-center",
            duration: 4000,
          });

          return;
        }
      }

      if (!isSummaryCompleted) {
        notCompleted.push("summary");
      }

      if (notCompleted.length > 0) {
        this.$toasted.show(
          "Oops! Some parts still need work: " + notCompleted.join(", "),
          {
            icon: "error",
            position: "bottom-center",
            duration: 3000,
          }
        );
        return;
      }

      if (this.chooseReadersStatus !== "Completed") {
        this.$toasted.show(
          "Please choose at least one option under 'Choose your readers' and click the blue 'Continue' button under those options before submiting your book.",
          {
            icon: "error",
            position: "bottom-center",
            duration: 5000,
          }
        );
        return;
      }

      if (this.requiresPlanUpgrade) {
        this.$toasted.show(
          "You'll need to upgrade your plan before you can submit this book.",
          {
            icon: "error",
            position: "bottom-center",
            duration: 3000,
          }
        );
        return;
      }

      Back.submitBook(this.book)
        .then((result) => {
          if (result.message) {
            this.$toasted.show(
              result.message,
              {
                icon: "error",
                position: "bottom-center",
                duration: 3000,
              }
            )
          } else {
          this.$toasted.show(
            "Great! We'll review " + this.book.title + " soon!",
            {
              icon: "check_circle",
              position: "bottom-center",
              duration: 3000,
            }
          );
          Bus.$emit("UserChanged", this.book);
          setTimeout(() => {
            this.$router.push("/author");
          }, 500);
        }
        })
        .catch((error) => {
          this.$toasted.show(
            "Oops! Something went wrong. Please try again later.",
            {
              icon: "error",
              position: "bottom-center",
              duration: 3000,
            }
          );
        });

      // console.log("Submit For Approval");
      // console.log(this.book.genre_id);
      // this.$validator.validateAll().then(result => {
      //   if (result) {
      //     console.log("Form Submitted!");
      //     return;
      //   }

      //   console.log("Correct them errors!");
      // });
    },
    fileChanged(file) {
      // handle file here. File will be an object.
      // If multiple prop is true, it will return an object array of files.
    },
    removeCover() {
      this.coverFileUrl = null;
      this.book.cover_file_url = null;
      this.save();
    },
    removeManuscript() {
      this.manuscriptFileUrl = null;
      this.book.manuscript_file_url = null;
      this.save();
    },
    toggleSummaryListOption(event) {
      if (
        !this.summaryAnswers[this.currentGenre.id.toString()][event.listName]
      ) {
        this.summaryAnswers[this.currentGenre.id.toString()][event.listName] =
          {};
      }

      this.summaryAnswers[this.currentGenre.id.toString()][event.listName][
        event.id
      ] = event.checked;

      this.save();
    },
    toggleExplicitContent(event) {
      this.explicitContent = event.checked;
      this.save();
    },
    toggleModal() {
      this.modalShow=!this.modalShow;
    }
  },
};

function newBook() {
  return {
    id: null,
    title: "",
    status: "Editing",
    genre_id: null,
    word_count: 0,
    summary: {},
  };
}

function findGenre(genres, id) {
  if (!genres || !id || genres.length === 0) {
    return null;
  }

  for (const g in genres) {
    if (genres[g].id === id) {
      return genres[g];
    }
  }

  return null;
}

function countWords(string) {
  if (!string) {
    return 0;
  }
  return string.trim().split(/\s+/).length;
}

function countObject(obj) {
  var size = 0,
    key;

  for (key in obj) {
    if (obj.hasOwnProperty(key) && obj[key]) {
      size++;
    }
  }

  return size;
}
</script>

<style scoped lang="scss">
.form-container {
  max-width: 914px;
  padding-top: 0px !important;
  padding-bottom: 80px !important;
  margin: 20px auto;
  font-family: "Source Sans Pro" !important;
  background: #fff;
}

.toolbar {
  position: fixed;
  z-index: 998;
  width: 100%;
  height: 74px;
  top: $top-bar-height;
  left: 0;
  background: #fff;
  border-bottom: 3px solid #f5f5f5;
  transition: 0.2s linear all;
  opacity: 0 !important;
}
.show-toolbar {
  opacity: 1 !important;
}
.toolbar-push {
  padding-left: $app-bar-width;
}
.toolbar-container {
  max-width: 1012px;
  margin: 0px auto;
  padding: 16px 0 !important;
  font-family: "Source Sans Pro";
}
.toolbar-title {
  padding-left: 58px;
  font-size: 20pt;
  font-weight: 600;
  color: #222;
}
.toolbar-progress {
  padding-top: 8px;
  text-align: center;
  font-size: 13pt;
  font-weight: 600;
  color: #2196f3;
}
.toolbar-status {
  padding-top: 8px;
  text-align: center;
  font-size: 13pt;
  font-weight: 600;
  color: #999;
}
.toolbar-space {
  height: 54px;
}
.save-message {
  color: #777;
  font-size: 13pt;
  padding: 30px 0 0 0;
  text-align: center;
  font-family: "Source Sans Pro";
}
@media (max-width: 1024px) {
  .toolbar-title {
    padding-left: 16px !important;
    height: auto !important;
  }
}

.section {
  padding: 12px 0;
}

.section-divider {
  width: 100%;
  height: 40px;
}

.section-heading {
  font-size: 21pt;
  font-weight: 500;
}

.section-instructions {
  padding-top: 0.25em;
  padding-left: 36px;
  padding-right: 80px !important;
  font-size: 13pt;
}

.section-status {
  float: left;
  width: 32px;
  margin: 12px 1px 12px 3px;
}
.section-status-circle {
  border: 3px solid #e3e3e3;
  border-radius: 32px;
  width: 21px;
  height: 21px;
}

.section-status-icon-checked {
  color: #fff;
  font-size: 11pt;
}
.section-status-circle-checked {
  background: #43a047 !important;
  border: 3px solid #43a047 !important;
}

.title-field {
  font-size: 17pt !important;
  border-bottom: 4px !important;
}

.container {
  width: 100%;
  padding: 0;
}
.head-container {
  width: 100%;
  background: #fff;
  border-bottom: 0px solid #f4f4f4;
}
.content {
  max-width: 1180px;
  margin: 0 auto;
  border-bottom: 0px solid #fff;
  padding: 24px 0 20px 0;
}
.actions {
  float: right;
}
.add-button {
  background: #2196f3 !important;
  margin-top: 10px;
  /*border: 2px solid #e3e3e3;*/
  /*border-bottom: 3px solid #c6c6c6;*/
  border-radius: 4px;
  padding: 7px 18px;
  font-size: 14pt;
  font-weight: 700;
  text-transform: none;
  color: #fff !important;
  font-family: "Source Sans Pro";
}
.plus {
  display: inline-block;
  padding: 0 0.2em 0 0;
}
.add-button .v-btn__content {
}
.subheading {
  padding-top: 2px;
  font-size: 14pt !important;
  font-weight: 700;
  color: #1565c0;
}
.action-button {
  float: left;
  margin-top: 8px;
  padding: 6px 16px;
  background: #fff;
  height: 32px;
  border-radius: 8px;
}

.prompt-field {
}
.prompt-sentence {
  font-size: 15pt;
  padding-top: 1.5em;
  padding-bottom: 0.25em;
  padding-left: 7px;
  font-weight: 600;
}
.prompt-examples {
  font-size: 14pt;
  font-style: italic;
  color: #999;
  padding-bottom: 0.25em;
  padding-left: 7px;
  font-weight: 600;
}
.prompt-answer {
  width: 100%;
  max-width: 740px;
  outline: 0;
  margin-bottom: 12px;
  border: 3px solid #fff;
  border-bottom: 2px solid #f4f4f4;
  border-radius: 4px;
  padding: 4px;
  font-size: 15pt;
}
.prompt-answer:hover {
  background: #f9f9f9;
}

.summary-list-option {
  float: left;
  width: 30%;
  height: 48px;
}

.summary-custom-list-item-container {
  float: left;
  width: 100%;
  max-width: 380px;
}
.summary-custom-list-item-number {
  float: left;
  width: 12px;
  padding-top: 18px;
}

.summary-custom-list-item {
  float: left;
  outline: 0;
  margin: 12px 20px 12px 0;
  width: 90%;
  max-width: 360px;
  border: 0;
  border-bottom: 1px solid #bbb;
  padding: 4px 8px 4px 2px;
  font-family: $font-family;
  font-size: 13pt;
}

.summary-error {
  color: darken(#f9a825, 10%);
  margin-top: 0px;
  padding-top: 0;
  padding-left: 0px;
  padding-right: 80px !important;
  font-size: 13.5pt;
  font-weight: 600;
}

.explicit-content {
  margin-left: 25%;
}

.remove-file {
  padding: 0em;
  text-align: center;
  font-size: 13pt;
  text-decoration: underline;
  color: $color-blue;
}

.cover-display {
  padding: 24px;
  padding-bottom: 12px;
  text-align: center;
}

.cover-display-img {
  width: 120px;
  box-shadow: 0 2px 5px 1px rgba(0, 0, 0, 0.2);
}

.manuscript-display {
  text-align: center;
  .manuscript-done {
    margin-top: 58px;
  }
  .manuscript-done-icon {
    font-size: 45pt;
    color: $color-blue;
  }
  .manuscript-done-filetype {
    width: 80px;
    margin: 20px auto;
    margin-top: 30px;
    background: #eee;
    border-radius: 32px;
    padding: 0.3em 1em !important;
    font-size: 13pt;
    font-weight: 700;
    color: $color-blue;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
}

.upload-instructions {
  font-size: 11.5pt;
  padding: 12px 0 0 0;
}

.submit-container {
  margin-top: -8px;
  text-align: center;
}
.submit-bottom-container {
  margin-top: 28px;
  margin-right: 22px;
  text-align: center;
}
.submit-button {
  display: inline-block;
  background: $color-blue;
  color: #fff;
  border-radius: 4px;
  padding: 8px 20px 9px 20px;
  font-size: 12pt;
  font-weight: 700;
  text-transform: none;
  font-family: "Source Sans Pro";
  text-transform: uppercase;
  text-align: center;
  letter-spacing: 1px;
  transition: all 0.12s ease-in-out;
}
.submit-button:hover {
  box-shadow: 0 0 0.3rem rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transform: scale(1.025);
}

.upgrade-required {
  margin: 66px auto 0 auto;
  max-width: 800px;
  background: $color-blue;
  border-radius: 16px;
  font-size: 13pt;
  font-weight: 700;
  color: #fff;
  padding: 16px 20px;
  display: flex;

  .upgrade-message {
    flex-grow: 1;
    padding-top: 8px;
  }

  .upgrade-button {
    flex-grow: 0;
    min-width: 160px;
    margin-left: auto;
    background: #fff;
    color: lighten($color-blue, 10%);
    padding: 0.5em 1em 0.4em 1em;
    text-align: center;
    border-radius: 4px;
    cursor: pointer;
  }
  .upgrade-button:hover {
    background: #fff;
    color: lighten($color-blue, 0%);
  }
}
@media (max-width: 640px) {
  .upgrade-required {
    display: block;
    margin: 12px 16px 8px 16px;
  }

  .toolbar-progress {
    padding-top: 4px;
  }

  .toolbar-progress-percentage {
    display: inline-block;
    text-align: center;
    background: #fff;
    border-radius: 16px;
    padding: 0 12px;
  }
}

.buttons {
  text-align: center;
  padding: 24px;
}
.continue-button {
  background: #daedff;
  color: #2196f3;
}

.example-img {
  // max-width:95px;
  // max-height:95px;
  width: 95px;
  height: auto;
  padding:10px;
}

.inline-icon {
   vertical-align: middle;
   font-size: 13px !important;
}

.blue-text {
  color:#0000EE;
  cursor: pointer;
  margin: 2px 0 2px 1em;
}

.emphasis {
  font-weight:550
}

</style>